import React from "react";
import { useHistory } from "react-router-dom";

/**
 * NewJobPostedCard — activity card for a newly posted job.
 *
 * Only Tier 2 and Tier 3 jobs generate this card (per product decision).
 * - Tier 2: Apply CTA only
 * - Tier 3: Refer Now (primary) + Apply (secondary) CTAs, green "GET PAID" badge,
 *   and a "Referral reward: X% of annual salary" line
 *
 * Props:
 *   card — feed card object with metadata containing:
 *     company_name, role_title, location, job_tier, tier_label,
 *     salary_range, currency, referral_reward_percent
 */
const NewJobPostedCard = ({ card }) => {
  const history = useHistory();
  const meta = card.metadata || {};
  const jobTier = parseInt(meta.job_tier, 10);
  const isTier3 = jobTier === 3;
  const jobId = card.cta_target_id;

  const handleApply = () => {
    if (jobId) {
      history.push(`/home/${jobId}`);
    }
  };

  const handleRefer = () => {
    if (jobId) {
      history.push(`/home/${jobId}/refer`);
    }
  };

  const companyInitial = (meta.company_name || "?").trim().charAt(0).toUpperCase();
  const formattedSalary = formatSalary(meta.salary_amount, meta.salary_range, meta.currency);
  const formattedReward = isTier3
    ? formatReferralReward(meta.referral_reward_amount, meta.referral_reward_percent, meta.currency)
    : null;

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div style={styles.labelGroup}>
          <span style={styles.tierLabel}>New Tier {jobTier || ""} Job</span>
          {isTier3 && <span style={styles.getPaidBadge}>GET PAID</span>}
        </div>
        <span style={styles.timestamp}>{formatTime(card.created_at)}</span>
      </div>

      <div style={styles.bodyRow}>
        <div style={styles.avatar}>{companyInitial}</div>

        <div style={styles.details}>
          <h6 style={styles.title}>{meta.role_title}</h6>
          <p style={styles.companyName}>{meta.company_name}</p>
          <p style={styles.location}>{meta.location}</p>
          {formattedSalary && <p style={styles.salary}>{formattedSalary}</p>}
          {formattedReward && <p style={styles.referralReward}>{formattedReward}</p>}
        </div>
      </div>

      <div style={styles.ctaRow}>
        {isTier3 && (
          <button style={styles.referBtn} onClick={handleRefer}>
            Refer Now
          </button>
        )}
        <button
          style={isTier3 ? styles.applyBtnSecondary : styles.applyBtnPrimary}
          onClick={handleApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

function formatTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffHrs = (now - date) / (1000 * 60 * 60);
  if (diffHrs < 1) return "Just now";
  if (diffHrs < 24) return `${Math.floor(diffHrs)}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return "1d ago";
  return `${diffDays}d ago`;
}

function formatSalary(salaryAmount, salaryRange, currency) {
  const symbol = currency === "NGN" ? "N" : currency === "USD" ? "$" : (currency || "");

  const abbreviate = (num) => {
    const n = parseFloat(num);
    if (isNaN(n)) return null;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}K`;
    return `${n}`;
  };

  // Prefer the real numeric salary amount
  if (salaryAmount && parseFloat(salaryAmount) > 0) {
    return `Salary: ${symbol}${abbreviate(salaryAmount)}/yr`;
  }

  // Fall back to salary_range string (e.g. "18000000-24000000")
  if (salaryRange && salaryRange !== "0") {
    const parts = String(salaryRange).split("-").map((p) => p.trim()).filter(Boolean);
    if (parts.length === 2) {
      return `Salary: ${symbol}${abbreviate(parts[0])} - ${symbol}${abbreviate(parts[1])}/yr`;
    }
    if (parts.length === 1) {
      return `Salary: ${symbol}${abbreviate(parts[0])}/yr`;
    }
  }

  return null;
}

function formatReferralReward(rewardAmount, rewardPercent, currency) {
  const symbol = currency === "NGN" ? "N" : currency === "USD" ? "$" : (currency || "");

  const abbreviate = (num) => {
    const n = parseFloat(num);
    if (isNaN(n)) return null;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}K`;
    return `${n}`;
  };

  // If we have the actual amount, show it
  if (rewardAmount && parseFloat(rewardAmount) > 0) {
    return `Referral reward: ${symbol}${abbreviate(rewardAmount)} (${rewardPercent}% of annual salary)`;
  }

  // Fall back to just the percentage
  if (rewardPercent) {
    return `Referral reward: ${rewardPercent}% of annual salary`;
  }

  return null;
}

const styles = {
  card: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    marginBottom: "16px",
    padding: "18px 20px",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "14px",
  },
  labelGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  tierLabel: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#64748b",
  },
  getPaidBadge: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#fff",
    backgroundColor: "#16a34a",
    padding: "3px 10px",
    borderRadius: "5px",
    letterSpacing: "0.3px",
  },
  timestamp: {
    fontSize: "12px",
    color: "#94a3b8",
  },
  bodyRow: {
    display: "flex",
    gap: "12px",
    marginBottom: "16px",
  },
  avatar: {
    width: "44px",
    height: "44px",
    borderRadius: "50%",
    backgroundColor: "#0f172a",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: "700",
    flexShrink: 0,
  },
  details: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#0f172a",
    margin: "0 0 2px 0",
  },
  companyName: {
    fontSize: "14px",
    color: "#334155",
    margin: "0 0 2px 0",
  },
  location: {
    fontSize: "13px",
    color: "#94a3b8",
    margin: "0 0 4px 0",
  },
  salary: {
    fontSize: "13px",
    color: "#64748b",
    margin: "0 0 4px 0",
  },
  referralReward: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#16a34a",
    margin: 0,
  },
  ctaRow: {
    display: "flex",
    gap: "10px",
  },
  referBtn: {
    flex: 1,
    padding: "10px 0",
    backgroundColor: "#3a6ed4",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
  },
  applyBtnSecondary: {
    flex: 1,
    padding: "10px 0",
    backgroundColor: "#fff",
    color: "#3a6ed4",
    border: "1px solid #3a6ed4",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  applyBtnPrimary: {
    flex: 1,
    padding: "10px 0",
    backgroundColor: "#3a6ed4",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
  },
};

export default NewJobPostedCard;
